import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import api from '@/services/api';
import './AuthPages.css';

interface ValidationError {
  field: string;
  message: string;
}

interface ResetPasswordResponse {
  status: string;
  message: string;
}

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [apiMessage, setApiMessage] = useState('');
  const [fade, setFade] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { email, reset_password_token} = location.state || {};

  useEffect(() => {
    if (!email || !reset_password_token) {
      navigate('/forgot-password');
      return;
    }

    setFade(true);
    const root = document.querySelector('#root');
    root?.classList.add('auth-background');

    return () => {
      root?.classList.remove('auth-background');
    };
  }, [email, reset_password_token, navigate]);

  useEffect(() => {
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password)
    });
  }, [password]);

  const getFieldError = (fieldName: string) => {
    return errors.find(error => error.field === fieldName)?.message;
  };

  const clearFieldError = (fieldName: string) => {
    setErrors(prev => prev.filter(error => error.field !== fieldName));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationError[] = [];

    if (!password) {
      newErrors.push({ field: 'password', message: 'Password is required' });
    } else {
      if (password.length < 8) {
        newErrors.push({ field: 'password', message: 'Password must be at least 8 characters' });
      }
      if (!/[A-Z]/.test(password)) {
        newErrors.push({ field: 'password', message: 'Password must contain at least one capital letter' });
      }
      if (!/[a-z]/.test(password)) {
        newErrors.push({ field: 'password', message: 'Password must contain at least one lowercase letter' });
      }
    }

    if (!confirmPassword) {
      newErrors.push({ field: 'confirmPassword', message: 'Please confirm your password' });
    } else if (password !== confirmPassword) {
      newErrors.push({ field: 'confirmPassword', message: 'Passwords do not match' });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage('');
    setErrors([]);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/reset-password', {
        email,
        password,
        password_confirmation: confirmPassword,
        reset_password_token
      });
      
      const result: ResetPasswordResponse = response.data as ResetPasswordResponse;

      if (result.status === "success") {
        setApiMessage(result.message || 'Password reset successfully!');
        
        setTimeout(() => {
          setFade(false);
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: 'Password reset successfully! Please login with your new password.' 
              } 
            });
          }, 300);
        }, 2000);
      } else {
        setApiMessage(result.message || 'Failed to reset password.');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.errors) {
          const validationErrors: ValidationError[] = [];
          Object.keys(errorData.errors).forEach(field => {
            errorData.errors[field].forEach((message: string) => {
              validationErrors.push({ field, message });
            });
          });
          setErrors(validationErrors);
        } else {
          setApiMessage(errorData.message || 'Failed to reset password.');
        }
      } else {
        setApiMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`auth-container min-w-full flex-col ${fade ? 'fade-in' : 'fade-out'}`}>
      <h1 className="auth-title mb-8">Reset New Password</h1>
      
      {apiMessage && (
        <div className={`message ${apiMessage.includes('success') ? 'message-success' : 'message-error'}`}>
          <div className="message-content">
            {apiMessage}
          </div>
        </div>
      )}

      <div className="auth-card">
        <div className="flex items-center justify-center mb-4">
            <LockClosedIcon className="auth-icon h-32 drop-shadow-[0_5px_5px_rgba(56,136,255,1)]" />
        </div>
        <p className="auth-subtitle font-normal">Please kindly set your new password</p>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password" className="form-label text-start">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                  getFieldError('password') ? 'input-error' : ''
                } pr-10`}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearFieldError('password');
                }}
                placeholder="Enter Password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors bg-transparent rounded-full"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            <div className="password-requirements">
              {/* <p className="requirement-title">Password Requirements:</p> */}
              <ul className="requirement-list text-start">
                <li className={passwordStrength.hasMinLength ? 'requirement-met' : ''}>
                  contains at least 8 characters
                </li>
                <li className={passwordStrength.hasUpperCase ? 'requirement-met' : ''}>
                  contains at least one capital letter
                </li>
                <li className={passwordStrength.hasLowerCase ? 'requirement-met' : ''}>
                  contains at least one lowercase letter
                </li>
              </ul>
            </div>
            
            {getFieldError('password') && (
              <div className="error-text">{getFieldError('password')}</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label text-start">Confirm Password</label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                  getFieldError('confirmPassword') ? 'input-error' : ''
                } pr-10`}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  clearFieldError('confirmPassword');
                }}
                placeholder="Retype Password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors bg-transparent rounded-full"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {getFieldError('confirmPassword') && (
              <div className="error-text">{getFieldError('confirmPassword')}</div>
            )}
          </div>
          
          <button 
            type="submit" 
            className={`auth-button rounded-full ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>

          <div className="auth-link">
            <span>Remember your password? </span>
            <Link to="/login" className="auth-link-text">Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;